import { ProviderNetworkType, EntityTypeEnum } from "@app/projects/enumerators";
import { AccuracyProvider, AccuracyProviderDetail } from "@app/projects/results/accuracy";
import { formatUnconvertedDateCustom } from "@app/shared";
import { RoleRestricted, Roles } from "@app/user";
import { Features, withFeatureFlagContext, FeatureFlagContextProps } from "@app/feature-flags";
import * as React from "react";
import { Link } from "react-router-dom";
import { getEntityIconClasses, getEntityTypeDescription } from "../ServicingProviderHelpers";
import { AccuracyProviderInspectionProfile } from "./AccuracyProviderInspectionProfile";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";

interface AccuracyProviderProfileProps {
  provider: AccuracyProvider;
  providerNetworkType: ProviderNetworkType;
  showAttestation: boolean;
  runConfigurationDate: Date;
}


interface AccuracyProviderProfileState {
  providerDetail: AccuracyProviderDetail;
}

class AccuracyProviderProfile extends React.Component<AccuracyProviderProfileProps & FeatureFlagContextProps, AccuracyProviderProfileState> {
  constructor(props: AccuracyProviderProfileProps & FeatureFlagContextProps) {
    super(props);
    this.state = {
      providerDetail: props.provider as AccuracyProviderDetail,
    };
  }

  public render() {
    const providerDetail = this.state.providerDetail;

    return (
      <div className="servicing-provider-profile">
        <section>
          <div className="row">
            <div className="col-sm-2">
              <h1 className="text-center">
                <i className={getEntityIconClasses(providerDetail.entityType, " text-very-large")} />
              </h1>
            </div>
            <div className="col-sm-8">
              <h3 className="pl0-imp mb-0">
                {providerDetail.displayName}
                {providerDetail.credentials && !providerDetail.displayName.includes(providerDetail.credentials) ? `, ${providerDetail.credentials}` : ""}
              </h3>
              {providerDetail.entityType ?
                <div>
                  <strong className="text-muted">{getEntityTypeDescription(providerDetail.entityType)}</strong>
                  < br />
                </div> :
                null
              }
              {providerDetail.address ? <>{providerDetail.address}<br /></> : null}
              {providerDetail.address2 ? <>{providerDetail.address2}<br /></> : null}
              {providerDetail.city}
              {providerDetail.city && (providerDetail.state || providerDetail.zip) ? ", " : null}
              {providerDetail.state} {providerDetail.zip}<br />
              {providerDetail.recommendedAction ?
                <div>
                  <strong>Recommended Action</strong> {providerDetail.recommendedAction.actionType}<br />
                  <strong>Reason</strong> {providerDetail.recommendedAction.reasons ? providerDetail.recommendedAction.reasons.join("; ") : ""}
                </div> :
                null
              }
              {this.renderAttestationStatus(providerDetail.lastAttestedDate)}
              <RoleRestricted allowedRoles={[Roles.ACCURACY_EVIDENCE_VIEWER]}>
                {this.renderEvidenceViewerLink(providerDetail)}
              </RoleRestricted>
            </div>
            <div className="col-sm-2">
              <div className="text-success">
                <QesIcon iconName={QesIcons.FontAwesome.Check} width="13" isDecorative classNames="ml-2 mr-1 align-baseline" /> = Valid
              </div>
              <div className="text-danger">
                <QesIcon iconName={QesIcons.FontAwesome.Times} width="10" isDecorative classNames="ml-2 mr-1 align-baseline" /> = Invalid
              </div>
              <div className="text-info">
                <QesIcon iconName={QesIcons.FontAwesome.Star} width="12" isDecorative classNames="ml-2 mr-1 align-baseline" /> = Enriched
              </div>
              <div className="text-muted">
                <QesIcon iconName={QesIcons.FontAwesome.Question} width="8" isDecorative classNames="ml-2 mr-1 align-baseline" /> = Unknown
              </div>
            </div>
          </div>
        </section>
        <section className="mb-0">
          <div className="row">
            <div className="col-12">
              <div className="tabs-style-2">
                <div className="tab-content">
                  {this.getTabContent(providerDetail)}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  private renderEvidenceViewerLink(providerDetails: AccuracyProviderDetail) {
    if (providerDetails.entityType && providerDetails.entityType == EntityTypeEnum.Facility.toLowerCase()) {
      return this.renderEvidenceViewerLinkForFacilities(providerDetails);
    }

    return this.renderEvidenceViewerLinkForPractitioners(providerDetails);
  }

  private renderEvidenceViewerLinkForFacilities(providerDetails: AccuracyProviderDetail) {
    const linkWithAddress = <Link to={`/tools/accuracyevidence?address1=${providerDetails.address}&address2=${providerDetails.address2 ? providerDetails.address2 : ""}&city=${providerDetails.city}&state=${providerDetails.state}&zip=${providerDetails.zip}&temporalPosition=${this.props.runConfigurationDate}`} target="_blank">Evidence</Link>;
    const linkWithXid = <Link to={`/tools/accuracyevidence?xid=${providerDetails.slXid}&temporalPosition=${this.props.runConfigurationDate}`} target="_blank">Evidence</Link>

    return (providerDetails.slXid && this.props.enabledFeatureFlags.has(Features.USE_XIDS_IN_QES_ACCURACY))
      ? linkWithXid
      : linkWithAddress;
  }

  private renderEvidenceViewerLinkForPractitioners(providerDetails: AccuracyProviderDetail) {

    const linkWithNPI = <Link to={`/tools/accuracyevidence?npi=${providerDetails.npi}&temporalPosition=${this.props.runConfigurationDate}`} target="_blank">Evidence</Link>;
    const linkWithXid = <Link to={`/tools/accuracyevidence?xid=${providerDetails.hcpXid}&temporalPosition=${this.props.runConfigurationDate}`} target="_blank">Evidence</Link>

    return (providerDetails.hcpXid && this.props.enabledFeatureFlags.has(Features.USE_XIDS_IN_QES_ACCURACY))
      ? linkWithXid
      : linkWithNPI;
  }

  private getTabContent = (providerDetail: AccuracyProviderDetail) => {
    return (<AccuracyProviderInspectionProfile {...this.props} provider={providerDetail} />);
  }

  private renderAttestationStatus(lastAttestedDate?: Date) {
    if (!this.props.showAttestation) {
      return;
    }

    return (
      <div>
        <strong>Last Attested Date</strong> {(lastAttestedDate && this.state.providerDetail.isRecentlyAttested) ? formatUnconvertedDateCustom(lastAttestedDate, "L") : "---"}<br />
      </div>
    );

  }
}

const wrappedAccuracyProviderProfile = withFeatureFlagContext(AccuracyProviderProfile);

export { wrappedAccuracyProviderProfile as AccuracyProviderProfile };